import React from 'react';
import { useUserContext } from 'src/context/UserProvider';
import { MyCompanyType, UIUser } from 'types';
import { Drawer } from '../shared/Drawer';
import { Loader } from 'ui';
import Container from '../shared/Container';
import NoRecruiterPositionsContainer from '../recruiterPositions/NoRecruiterPositionsContainer';
import LeadNewCompanyDrawerHeaderFooter from './LeadNewCompanyDrawerHeaderFooter';
import { HiBriefcase, HiClock, HiCurrencyRupee, HiLocationMarker, HiPhone } from 'react-icons/hi';
import { Label } from 'flowbite-react';
import { FaUser } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';

interface Props {
    isPositionDrawerOpen: boolean;
    company: MyCompanyType['company'];
    setIsPositionDrawerOpen: (val: boolean) => void;
    closeDrawer: () => void;
}

export interface PositionSkill {
    id: string;
    name: string;
    threshold: number;
    rating: number;
}

const LeadViewDetailsDrawer = ({ isPositionDrawerOpen, closeDrawer, company, setIsPositionDrawerOpen }: Props) => {
    const user = useUserContext();

    const LeadGenMetaData = Array.isArray(company.leadGenMeta) ? company.leadGenMeta[0] : undefined;

    return (
        <Drawer
            isOpen={isPositionDrawerOpen}
            onClose={() => {
                setIsPositionDrawerOpen(false);
            }}
            header={
                <>
                    {!company?.leadGenMeta ? (
                        <div className="mt-8">
                            <Loader />
                        </div>
                    ) : company?.leadGenMeta ? (
                        <>
                            <Container className="!px-0 xl:!px-[80px]">
                                <LeadNewCompanyDrawerHeaderFooter
                                    setIsPositionDrawerOpen={setIsPositionDrawerOpen}
                                    companyDetail={LeadGenMetaData}
                                    isFooter={false}
                                    userDetail={user.user as UIUser}
                                    closeDrawer={closeDrawer}
                                />
                                <div className="flex justify-center">
                                    <hr className="h-[1px] w-full rounded-[32px] border-0 bg-[#C7C9D9] dark:bg-[#8F90A6]" />
                                </div>
                            </Container>
                        </>
                    ) : (
                        <div className="flex align-center justify-center pt-[36px]">
                            <NoRecruiterPositionsContainer
                                showAction={false}
                                title="Details Not found"
                                description="The details for this position are not available at the moment as recruiter has not created any position yet."
                            />
                        </div>
                    )}
                </>
            }
            body={
                <>
                    <div className="mx-[-24px] md:mx-0 mt-[20px] md:mt-3 px-[62px] py-[12px]">
                        <div className="flex flex-col md:flex-row gap-[10px] lg:gap-[3rem]">
                            <div className="flex flex-wrap gap-x-[20%] md:gap-x-[16px] gap-y-[12px] pt-[7px]">
                                <div className="flex items-center">
                                    <HiBriefcase className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
                                    <div className="ml-1 flex items-center gap-1">{LeadGenMetaData.positionType}</div>
                                </div>


                                {LeadGenMetaData?.workExperience ? (
                                    <div className="flex items-center">
                                        <HiClock className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
                                        <div className="ml-1">{`${LeadGenMetaData.workExperience.min === LeadGenMetaData.workExperience.max
                                            ? LeadGenMetaData.workExperience.max
                                            : `${LeadGenMetaData.workExperience.min} - ${LeadGenMetaData.workExperience.max}`
                                            }`}{' '}
                                            Years</div>
                                    </div>
                                ) : '-'}

                                {LeadGenMetaData && LeadGenMetaData?.salary ? (
                                    <div>
                                        <div className="flex items-center">
                                            <HiCurrencyRupee className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
                                            <div className="ml-1">
                                                {`${LeadGenMetaData.salary.min === LeadGenMetaData.salary.max
                                                    ? LeadGenMetaData.salary.max
                                                    : `${LeadGenMetaData.salary.min} - ${LeadGenMetaData.salary.max}`
                                                    }`}{' '}
                                                LPA
                                            </div>
                                        </div>
                                    </div>
                                ) : null}

                                <div className="flex items-center">
                                    <HiLocationMarker className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
                                    <div className="ml-1 flex items-center gap-1 flex-wrap">
                                        {LeadGenMetaData?.workLocations?.join(', ')}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='flex flex-col gap-2 mt-1'>
                            {LeadGenMetaData?.techStack && (
                                <div className="flex items-center gap-2">
                                    <Label className="text-[#000] text-[16px] font-[700] leading-[24px] dark:text-[#474848]">Skills :</Label>
                                    <div className="flex items-center gap-1">{LeadGenMetaData.techStack.join(', ')}</div>
                                </div>
                            )}

                            {LeadGenMetaData?.leadGen?.bdPOC && (
                                <div className="flex flex-col">
                                    <Label className="text-[#000] text-[16px] font-[700] leading-[24px] dark:text-[#474848]">BD POC Details :</Label>
                                    <div className="flex items-center">
                                        <div>
                                            <FaUser className="text-[#00B7C4] dark:text-[#A9EFF2]" />
                                        </div>
                                        <div className="ml-1 flex gap-1 items-center sm:text-[12px] text-[14px] text-[#28293D] dark:text-[#E4E4EB] font-[500]">
                                            <div className="dark:text-[#E4E4EB] font-bold">BD POC Name:</div>
                                            <div className="text-sm text-[#28293D] dark:text-[#E4E4EB]">{LeadGenMetaData.leadGen?.bdPOC.name}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <div>
                                            <MdEmail className="text-[#00B7C4] dark:text-[#A9EFF2]" />
                                        </div>
                                        <div className="ml-1 flex gap-1 items-center sm:text-[12px] text-[14px] text-[#28293D] dark:text-[#E4E4EB] font-[500]">
                                            <div className="dark:text-[#E4E4EB] font-bold">Email:</div>
                                            <div className="text-sm text-[#28293D] dark:text-[#E4E4EB]">{LeadGenMetaData.leadGen?.bdPOC.email}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <div>
                                            <HiPhone className="text-[#00B7C4] dark:text-[#A9EFF2]" />
                                        </div>
                                        <div className="ml-1 flex gap-1 items-center sm:text-[12px] text-[14px] text-[#28293D] dark:text-[#E4E4EB] font-[500]">
                                            <div className="dark:text-[#E4E4EB] font-bold">Phone:</div>
                                            <div className="text-sm text-[#28293D] dark:text-[#E4E4EB]">{LeadGenMetaData.leadGen?.bdPOC.phone}</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {LeadGenMetaData?.leadGen?.leadGenPOC && (

                                <div className="flex flex-col">
                                    <Label className="text-[#000] text-[16px] font-[700] leading-[24px] dark:text-[#474848]">LEAD GEN POC Details :</Label>
                                    <div className="flex items-center">
                                        <div>
                                            <FaUser className="text-[#00B7C4] dark:text-[#A9EFF2]" />
                                        </div>
                                        <div className="ml-1 flex gap-1 items-center sm:text-[12px] text-[14px] text-[#28293D] dark:text-[#E4E4EB] font-[500]">
                                            <div className="dark:text-[#E4E4EB] font-bold">LeadGen POC Name:</div>
                                            <div className="text-sm text-[#28293D] dark:text-[#E4E4EB]">{LeadGenMetaData.leadGen?.leadGenPOC.name}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <div>
                                            <MdEmail className="text-[#00B7C4] dark:text-[#A9EFF2]" />
                                        </div>
                                        <div className="ml-1 flex gap-1 items-center sm:text-[12px] text-[14px] text-[#28293D] dark:text-[#E4E4EB] font-[500]">
                                            <div className="dark:text-[#E4E4EB] font-bold">Email:</div>
                                            <div className="text-sm text-[#28293D] dark:text-[#E4E4EB]">{LeadGenMetaData.leadGen?.leadGenPOC.email}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <div>
                                            <HiPhone className="text-[#00B7C4] dark:text-[#A9EFF2]" />
                                        </div>
                                        <div className="ml-1 flex gap-1 items-center sm:text-[12px] text-[14px] text-[#28293D] dark:text-[#E4E4EB] font-[500]">
                                            <div className="dark:text-[#E4E4EB] font-bold">Phone:</div>
                                            <div className="text-sm text-[#28293D] dark:text-[#E4E4EB]">{LeadGenMetaData.leadGen?.leadGenPOC.phone}</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                </>
            }
        />
    );
};

export default LeadViewDetailsDrawer;
